import { AnimatePresence, motion } from "framer-motion";
import { Camera } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import Logo from "../../assets/logo_klikin.png";

const steps = ["ready", 3, 2, 1, "capture"];

export default function CapturePage() {
  const navigate = useNavigate();

  const webcamRef = useRef(null);

  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isFlash, setIsFlash] = useState(false);

  const currentStep = steps[stepIndex];

  useEffect(() => {
    const video = webcamRef.current?.video;

    if (video) {
      video.onloadedmetadata = () => {
        console.log(
          "Actual Resolution:",
          video.videoWidth,
          "x",
          video.videoHeight
        );
      };
    }
  }, []);

  const photoboothData =
    JSON.parse(localStorage.getItem("photobooth_data")) || {};

  const answers = photoboothData.answers || {};

  const dreamJob = answers.template?.label || "Dreamer";
  const inspiration = answers.inspiration?.label || "";
  const dreamWorld = answers.dream_world?.label || "";

  const captureImage = useCallback(() => {
    const video = webcamRef.current?.video;

    if (!video) return;

    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;

    // Rasio portrait 9:16
    const targetRatio = 9 / 16;
    const sourceRatio = sourceWidth / sourceHeight;

    let cropWidth;
    let cropHeight;
    let cropX;
    let cropY;

    if (sourceRatio > targetRatio) {
      cropHeight = sourceHeight;
      cropWidth = cropHeight * targetRatio;

      cropX = (sourceWidth - cropWidth) / 2;
      cropY = 0;
    } else {
      cropWidth = sourceWidth;
      cropHeight = cropWidth / targetRatio;

      cropX = 0;
      cropY = (sourceHeight - cropHeight) / 2;
    }

    // Gunakan ukuran hasil crop ASLI
    const canvas = document.createElement("canvas");

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Mirror seperti preview webcam
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);

    ctx.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // PNG biasanya lebih tajam daripada JPEG
    const image = canvas.toDataURL("image/png");

    try {
      const existingData =
        JSON.parse(localStorage.getItem("photobooth_data")) || {};

      localStorage.setItem(
        "photobooth_data",
        JSON.stringify({
          ...existingData,
          captured_photo: image,
        })
      );

      navigate("/generate");
    } catch (err) {
      console.error(err);
    }
  }, [navigate]);

  const handleCapture = useCallback(() => {
    setTimeout(() => {
      setIsFlash(true);

      setTimeout(() => {
        captureImage();
        setIsFlash(false);
      }, 300);
    }, 500);
  }, [captureImage]);

  useEffect(() => {
    if (!started) return;

    if (currentStep === "capture") {
      handleCapture();
      return;
    }

    const timer = setTimeout(() => {
      setStepIndex((prev) => prev + 1);
    }, currentStep === "ready" ? 1800 : 1000);

    return () => clearTimeout(timer);
  }, [currentStep, started, handleCapture]);

  const startCapture = () => {
    setStarted(true);
  };

  return (
    <div className="relative h-full w-full overflow-hidden bg-black text-white">
      {/* CAMERA */}
      <Webcam
  ref={webcamRef}
  audio={false}
  mirrored
  playsInline
  screenshotFormat="image/png"
  forceScreenshotSourceSize
  videoConstraints={{
    facingMode: "user",
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 },
  }}
  className="absolute inset-0 w-full h-full object-cover"
/>

      {/* OVERLAY */}
      <div className="absolute inset-0 bg-black/35" />

      {/* TOP GRADIENT */}
      <div className="absolute top-0 left-0 h-[300px] w-full bg-gradient-to-b from-black via-black/60 to-transparent" />

      {/* BOTTOM GRADIENT */}
      <div className="absolute bottom-0 left-0 h-[320px] w-full bg-gradient-to-t from-black via-black/80 to-transparent" />

      {/* HEADER */}
      <div className="absolute top-0 left-0 right-0 z-20 px-6 pt-10">
        <div className="text-center">
          <p className="flex justify-center items-center text-xs font-bold tracking-[0.4em] text-violet-300">
          <img src={Logo} className="h-10 w-auto object-fit-contain" />
          </p>

          {/* <h1 className="mt-3 text-3xl font-black">
            FIND YOUR DREAM
          </h1> */}
        </div>
      </div>

      {/* CENTER MESSAGE */}
      {!started && (
        <div className="absolute inset-x-0 top-1/2 z-10 -translate-y-1/2 px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-violet-300">
            Imagine Yourself Becoming
            </p>

            <h2 className="text-5xl font-black leading-tight">
              {dreamJob}
            </h2>

            <p className="mt-6 text-lg leading-8 text-white/80">
            Pose as your dream version

              <br />
              and show your best expression
            </p>
          </motion.div>
        </div>
      )}

      {/* BOTTOM */}
      <div className="absolute bottom-12 left-0 right-0 z-20 px-8">
        {!started ? (
          <motion.button
            whileTap={{ scale: 0.96 }}
            whileHover={{ scale: 1.01 }}
            onClick={startCapture}
            className="flex h-[82px] w-full items-center justify-center gap-4 rounded-3xl bg-violet-600 text-xl font-black uppercase tracking-wide shadow-[0_0_50px_rgba(139,92,246,0.45)]"
          >
            <Camera size={28} />
            Start Photo
          </motion.button>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.25, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center"
            >
              {currentStep === "ready" ? (
                <>
                  <p className="mb-3 text-sm uppercase tracking-[0.3em] text-violet-300">
                    Become A
                  </p>

                  <h2 className="text-center text-4xl font-black">
                    {dreamJob}
                  </h2>
                </>
              ) : currentStep !== "capture" ? (
                <motion.div
  initial={{ scale: 0.5, opacity: 0 }}
  animate={{
    scale: [0.8, 1.1, 1],
    opacity: 1,
  }}
  transition={{
    duration: 0.8,
  }}
  className="relative"
>
  {/* Glow */}
  <div className="absolute inset-0 blur-3xl">
    <div className="text-[180px] font-black text-violet-500/40">
      {currentStep}
    </div>
  </div>

  {/* Main Number */}
  <div className="relative bg-gradient-to-b from-white via-violet-200 to-violet-500 bg-clip-text text-[180px] font-black leading-none text-transparent">
    {currentStep}
  </div>

  {/* Ring */}
  <motion.div
    animate={{
      scale: [1, 1.3],
      opacity: [0.4, 0],
    }}
    transition={{
      duration: 1,
      repeat: Infinity,
    }}
    className="absolute inset-0 rounded-full border border-violet-400"
  />
</motion.div>
              ) : (
                <div className="text-center">
                  <p className="text-sm uppercase tracking-[0.3em] text-violet-300">
                    Creating Your Dream
                  </p>

                  <h2 className="mt-3 text-4xl font-black">
                    Capturing...
                  </h2>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* FLASH */}
      {isFlash && (
        <div className="absolute inset-0 z-50 animate-pulse bg-white" />
      )}
    </div>
  );
}